<template>
  <div class="iban-input-wrapper">
    <div class="iban-input-container" :class="{ 'valid': isValid && fullValue.length === 24, 'invalid': showError && !isValid && fullValue.length === 24 }">
      <span class="iban-prefix">ES</span>
      <input
        type="text"
        :value="displayValue"
        @input="handleInput"
        @paste="handlePaste"
        @blur="validateOnBlur"
        v-bind="$attrs"
        maxlength="22"
        placeholder="22 dígitos"
      />
    </div>
    <span v-if="isValid && fullValue.length === 24" class="validation-icon valid-icon">✓</span>
    <span v-if="showError && !isValid && fullValue.length === 24" class="validation-icon error-icon">✗</span>
  </div>
</template>

<script>
export default {
  name: 'SpanishIbanInput',
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showError: false
    };
  },
  computed: {
    displayValue() {
      // Show only the digits (without ES prefix)
      if (!this.value) return '';
      const withoutES = this.value.startsWith('ES') ? this.value.slice(2) : this.value;
      return withoutES.replace(/[^0-9]/g, '');
    },
    fullValue() {
      // Full IBAN value including ES prefix
      const digits = this.displayValue;
      return 'ES' + digits;
    },
    isValid() {
      if (!this.fullValue || this.fullValue.length !== 24) return false;
      return this.validateIbanChecksum(this.fullValue);
    }
  },
  methods: {
    validateIbanChecksum(iban) {
      // Spanish IBAN must be 24 characters starting with ES
      if (!iban || iban.length !== 24 || !iban.startsWith('ES')) {
        return false;
      }

      // Move first 4 characters to the end
      const rearranged = iban.slice(4) + iban.slice(0, 4);
      
      // Replace letters with numbers (A=10, B=11, ..., Z=35)
      let numericString = '';
      for (let char of rearranged) {
        if (char >= '0' && char <= '9') {
          numericString += char;
        } else {
          // Convert letter to number (A=10, B=11, etc.)
          numericString += (char.charCodeAt(0) - 55).toString();
        }
      }
      
      // Calculate mod 97
      // For large numbers, we need to calculate mod in chunks
      let remainder = 0;
      for (let i = 0; i < numericString.length; i++) {
        remainder = (remainder * 10 + parseInt(numericString[i])) % 97;
      }
      
      return remainder === 1;
    },

    validateOnBlur() {
      if (this.fullValue.length === 24) {
        this.showError = true;
        this.$emit('validation-change', this.isValid);
      }
    },

    handleInput(event) {
      let value = event.target.value;
      
      // Extract only digits
      const digitsOnly = value.replace(/[^0-9]/g, '');
      
      // Limit to 22 digits
      const limitedDigits = digitsOnly.slice(0, 22);
      
      // Construct final value with ES prefix
      const finalValue = 'ES' + limitedDigits;
      
      // Reset error when user types
      if (this.showError && finalValue.length < 24) {
        this.showError = false;
      }
      
      this.$emit('input', finalValue);
      
      // Emit validation status when complete
      if (finalValue.length === 24) {
        this.$emit('validation-change', this.validateIbanChecksum(finalValue));
      }
    },
    
    handlePaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      
      // Extract only digits from pasted text (remove ES if present)
      let textToProcess = pastedText;
      if (pastedText.toUpperCase().startsWith('ES')) {
        textToProcess = pastedText.slice(2);
      }
      const digitsOnly = textToProcess.replace(/[^0-9]/g, '');
      
      // Limit to 22 digits
      const limitedDigits = digitsOnly.slice(0, 22);
      
      // Construct final value
      const finalValue = 'ES' + limitedDigits;
      
      this.$emit('input', finalValue);
      
      // Set cursor to end
      this.$nextTick(() => {
        event.target.setSelectionRange(limitedDigits.length, limitedDigits.length);
      });
    }
  },
  mounted() {
    // Initialize with "ES" if empty or ensure it starts with ES
    if (!this.value || this.value === '') {
      this.$emit('input', 'ES');
    } else if (!this.value.startsWith('ES')) {
      // Ensure existing value starts with "ES"
      const digitsOnly = this.value.replace(/[^0-9]/g, '');
      this.$emit('input', 'ES' + digitsOnly.slice(0, 22));
    }
  },
  watch: {
    value(newValue) {
      // Ensure value always starts with ES
      if (newValue && !newValue.startsWith('ES')) {
        const digitsOnly = newValue.replace(/[^0-9]/g, '');
        this.$emit('input', 'ES' + digitsOnly.slice(0, 22));
      }
    }
  }
}
</script>

<style scoped>
.iban-input-wrapper {
  position: relative;
  width: 100%;
}

.iban-input-container {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-outline);
  border-radius: 8px;
  background-color: var(--color-background);
  transition: all 0.3s;
  overflow: hidden;
}

.iban-input-container:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(0, 174, 221, 0.1);
}

.iban-input-container.valid {
  border-color: var(--success);
  background-color: rgba(67, 160, 71, 0.05);
}

.iban-input-container.invalid {
  border-color: var(--error);
  background-color: rgba(244, 67, 54, 0.05);
}

.iban-prefix {
  padding: 12px 8px 12px 15px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  background-color: var(--color-active-surface);
  user-select: none;
  letter-spacing: 0.5px;
}

input {
  flex: 1;
  padding: 12px 40px 12px 8px;
  border: none;
  font-size: 14px;
  background-color: transparent;
  color: var(--color-text-primary);
  outline: none;
  min-width: 0;
}

input::placeholder {
  color: var(--color-text-inactive);
  font-size: 13px;
}

input:disabled {
  color: var(--color-text-inactive);
  cursor: not-allowed;
}

.iban-input-container:has(input:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
}

.iban-input-container:has(input:disabled) .iban-prefix {
  opacity: 0.6;
}

.validation-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  font-weight: bold;
  pointer-events: none;
  z-index: 1;
}

.valid-icon {
  color: var(--success);
}

.error-icon {
  color: var(--error);
}
</style>

